import { useEffect, useRef, useState } from 'react';

const hlsConfig = {
  maxBufferLength: 60,
  maxMaxBufferLength: 600,
  maxBufferSize: 60 * 1024 * 1024,
  liveSyncDurationCount: 3,
  highBufferWatchdogPeriod: 2,
  manifestLoadingTimeOut: 20000,
  manifestLoadingMaxRetry: 4,
  levelLoadingTimeOut: 20000,
  levelLoadingMaxRetry: 4,
  fragLoadingTimeOut: 20000,
  fragLoadingMaxRetry: 6,
};

export default function StreamPlayer() {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const retryCountRef = useRef(0);
  const shouldResumeRef = useRef(false);
  const reconnectingRef = useRef(false);
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [quality, setQuality] = useState('');
  const [qualityMode, setQualityMode] = useState('auto');
  const [qualityLevels, setQualityLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [playbackRetry, setPlaybackRetry] = useState(0);
  const filteredChannels = channels.filter((channel) =>
    channel.title.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));

  useEffect(() => {
    let active = true;
    fetch('/api/playlist')
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || `Erreur ${response.status}`);
        if (!Array.isArray(body.channels) || body.channels.length === 0) {
          throw new Error('Aucune chaîne disponible dans la playlist TV du serveur.');
        }
        if (!active) return;
        const available = body.channels.map((channel) => ({
          ...channel,
          url: `/api/transcode/channel/${channel.id}`,
        }));
        setChannels(available);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Impossible de charger la playlist.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selected) return undefined;
    const controller = new AbortController();
    let hls;
    let cancelled = false;
    let mediaRecoveryAttempted = false;
    let reconnectTimer;
    let stallTimer;
    const scheduleReconnect = () => {
      if (cancelled || reconnectTimer) return;
      if (!video.paused) shouldResumeRef.current = true;
      reconnectingRef.current = true;
      setReconnecting(true);
      setError('');
      const delay = Math.min(2000 * (2 ** retryCountRef.current), 30_000);
      retryCountRef.current += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        setPlaybackRetry((attempt) => attempt + 1);
      }, delay);
    };
    const clearStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = null;
    };
    const handleWaiting = () => {
      if (video.paused || stallTimer) return;
      const currentTime = video.currentTime;
      stallTimer = setTimeout(() => {
        stallTimer = null;
        if (!cancelled && !video.paused && video.readyState < 3 && video.currentTime === currentTime) {
          scheduleReconnect();
        }
      }, 12_000);
    };
    const handlePlaying = () => {
      clearStallTimer();
      retryCountRef.current = 0;
      shouldResumeRef.current = true;
      reconnectingRef.current = false;
      setReconnecting(false);
      setError('');
    };
    const handlePlay = () => { shouldResumeRef.current = true; };
    const handlePause = () => {
      if (!reconnectingRef.current) shouldResumeRef.current = false;
    };
    const handleMediaError = () => scheduleReconnect();
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('stalled', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('error', handleMediaError);
    setError('');
    setQuality('');
    setQualityLevels([]);
    if (playbackRetry === 0) setReconnecting(false);
    fetch(selected.url, { signal: controller.signal })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(body.error || `Erreur ${response.status}`);
          error.retryable = response.status >= 500;
          throw error;
        }
        if (typeof body.url !== 'string') {
          const error = new Error('Le serveur n’a pas préparé le flux vidéo.');
          error.retryable = false;
          throw error;
        }
        return body.url;
      })
      .then(async (manifestUrl) => {
        if (cancelled) return;
        const { default: Hls } = await import('hls.js');
        if (cancelled) return;
        if (!Hls.isSupported()) throw new Error('Ce navigateur ne prend pas en charge HLS.js.');
        hls = new Hls(hlsConfig);
        hlsRef.current = hls;
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          const level = hls.levels[hls.currentLevel];
          setQualityLevels(hls.levels.map((item, index) => ({
            index,
            height: item.height,
          })));
          setQuality(level?.height ? `${level.height}p` : '');
          if (shouldResumeRef.current) {
            video.play().catch(() => {
              setError('Le flux est prêt. Appuyez sur lecture pour reprendre.');
            });
          }
        });
        hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
          const level = hls.levels[data.level];
          if (level?.height) setQuality(`${level.height}p`);
        });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          const silentRecoveryDetails = [
            Hls.ErrorDetails.BUFFER_STALLED_ERROR,
            Hls.ErrorDetails.BUFFER_NOSTART_ERROR,
            Hls.ErrorDetails.BUFFER_SEEK_OVER_HOLE,
            Hls.ErrorDetails.BUFFERING_ERROR,
            Hls.ErrorDetails.KEY_LOAD_ERROR,
            Hls.ErrorDetails.LEVEL_LOAD_ERROR,
            Hls.ErrorDetails.MANIFEST_LOAD_ERROR,
            Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT,
            Hls.ErrorDetails.MANIFEST_PARSING_ERROR,
            Hls.ErrorDetails.FRAG_LOAD_ERROR,
            Hls.ErrorDetails.FRAG_LOAD_TIMEOUT,
          ];

          const transientFailure = !data.fatal && silentRecoveryDetails.includes(data.details);
          if (transientFailure) {
            if (!mediaRecoveryAttempted) {
              mediaRecoveryAttempted = true;
              hls.recoverMediaError();
            }
            return;
          }

          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !mediaRecoveryAttempted) {
            mediaRecoveryAttempted = true;
            hls.recoverMediaError();
            return;
          }
          scheduleReconnect();
        });
        hls.loadSource(manifestUrl);
        hls.attachMedia(video);
      })
      .catch((err) => {
        if (!cancelled && err.name !== 'AbortError') {
          if (err.retryable === false || err.message?.includes('ne prend pas en charge HLS.js')) {
            setError(err.message || 'Impossible de préparer la lecture.');
          } else {
            scheduleReconnect();
          }
        }
      });
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(reconnectTimer);
      clearStallTimer();
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('stalled', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('error', handleMediaError);
      hls?.destroy();
      if (hlsRef.current === hls) hlsRef.current = null;
      video.removeAttribute('src');
      video.load();
    };
  }, [selected, playbackRetry]);

  return (
    <section className="card stream-page">
      <div className="stream-heading">
        <div>
          <span className="stream-eyebrow">BERFOOT TV</span>
          <h1>Télévision en direct</h1>
        </div>
        <span className="stream-live"><i /> DIRECT</span>
      </div>

      <div className="stream-screen">
        {selected ? (
          <video
            ref={videoRef}
            className="stream-video"
            controls
            playsInline
          />
        ) : (
          <div className="stream-placeholder">
            <span className="stream-play-icon">▶</span>
            <p>{loading ? 'Chargement des chaînes…' : 'Sélectionnez une chaîne pour commencer'}</p>
          </div>
        )}
      </div>

      <div className="stream-now">
        <div>
          <span className="stream-eyebrow">À L’ANTENNE</span>
          <h2>{selected?.title ?? (loading ? 'Préparation du direct…' : 'Aucune chaîne sélectionnée')}</h2>
          {selected && (
            <label className="stream-quality-control" htmlFor="stream-quality">
              <span>Qualité</span>
              <select
                id="stream-quality"
                className="stream-quality-select"
                value={qualityMode}
                disabled={!qualityLevels.length}
                onChange={(event) => {
                  const { value } = event.target;
                  setQualityMode(value);
                  if (hlsRef.current) {
                    hlsRef.current.currentLevel = value === 'auto' ? -1 : Number(value);
                  }
                }}
              >
                <option value="auto">{`Automatique${quality ? ` · ${quality}` : ''}`}</option>
                {qualityLevels.map(({ index, height }) => (
                  <option key={index} value={index}>{height ? `${height}p` : `Qualité ${index + 1}`}</option>
                ))}
              </select>
            </label>
          )}
        </div>
        <span className="stream-channel-count">{channels.length} chaînes</span>
      </div>

      {reconnecting && <p className="stream-reconnecting" role="status">Reconnexion au flux en cours…</p>}
      {error && <p className="stream-error" role="alert">{error}</p>}

      <div className="stream-picker">
        <div className="stream-picker-heading">
          <div>
            <span className="stream-eyebrow">PROGRAMME</span>
            <h2>Choisir une chaîne</h2>
          </div>
          {loading && <span className="stream-loading" role="status">Chargement…</span>}
        </div>
        <div className="stream-picker-controls">
          <label className="stream-search">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              placeholder="Rechercher une chaîne…"
              aria-label="Rechercher une chaîne"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label className="stream-select-label" htmlFor="stream-channel">Chaîne</label>
          <select
            id="stream-channel"
            className="stream-select"
            value={selected?.id ?? ''}
            disabled={loading || filteredChannels.length === 0}
            onChange={(event) => {
              const channel = channels.find((item) => item.id === Number(event.target.value));
              if (channel) {
                retryCountRef.current = 0;
                shouldResumeRef.current = true;
                reconnectingRef.current = false;
                setPlaybackRetry(0);
                setReconnecting(false);
                setQualityMode('auto');
                setSelected(channel);
              }
            }}
          >
            {filteredChannels.length === 0
              ? selected
                ? <option value={selected.id}>{selected.title} · à l’antenne</option>
                : <option value="">{search ? 'Aucun résultat' : 'Aucune chaîne disponible'}</option>
              : (
                <>
                  {selected && !filteredChannels.some((channel) => channel.id === selected.id)
                    && <option value={selected.id}>{selected.title} · à l’antenne</option>}
                  {filteredChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>{channel.title}</option>
                  ))}
                </>
              )}
          </select>
        </div>
        <p className="stream-results">
          {search
            ? `${filteredChannels.length} résultat${filteredChannels.length === 1 ? '' : 's'}`
            : 'La lecture démarre en basse qualité et s’adapte automatiquement à votre connexion.'}
        </p>
      </div>
    </section>
  );
}
